import { createResponseSchema } from '@mint-sdk/api-core';
import z from 'zod/v4';

import { FlowTypesEnum, EntitySourcesEnum } from '../shared/schemas/index.js';

const BaseRequestSchema = z.object({
  approvalFlowId: z.string(),
  comment: z.string(),
  ccEmails: z.array(z.email()),
});

const EntityMetadataSchema = z.object({
  trackedFields: z.record(z.string(), z.unknown()),
});

const FlightEntityRequestSchema = BaseRequestSchema.extend({
  entityType: z.literal(FlowTypesEnum.enum.flight),
  entityId: z.string(),
  entityName: z.string(),
  entitySource: z.literal(EntitySourcesEnum.enum['planning-service']),
  entityMetadata: EntityMetadataSchema,
});

export const CreateRequestRequestSchema = z.discriminatedUnion('entityType', [
  FlightEntityRequestSchema,
]);

export type CreateRequestPayload = z.infer<typeof CreateRequestRequestSchema>;

const ApprovalCandidateSchema = z.object({
  userId: z.number(),
  email: z.email(),
  firstName: z.string(),
  lastName: z.string(),
  status: z.enum(['pending', 'approved', 'rejected', 'excluded']),
  actedAt: z.iso.datetime().nullable(),
  comment: z.string(),
});

const ApproverSchema = z.object({
  id: z.string(),
  name: z.string(),
  approverType: z.enum(['role', 'user']),
  status: z.enum(['pending', 'approved', 'rejected']),
  candidates: z.array(ApprovalCandidateSchema),
  resolvedAt: z.iso.datetime().nullable(),
});

const StageSchema = z.object({
  id: z.string(),
  index: z.number(),
  status: z.enum(['active', 'inactive', 'approved', 'rejected']),
  approvers: z.array(ApproverSchema),
});

const RequestorSchema = z.object({
  userId: z.number(),
  email: z.email(),
  firstName: z.string(),
  lastName: z.string(),
  comment: z.string(),
});

export const ExecutionItemSchema = z.object({
  id: z.string(),
  approvalFlowId: z.string(),
  workspaceId: z.number(),
  entityType: FlowTypesEnum,
  entityId: z.string(),
  entityName: z.string(),
  entitySource: z.string(),
  entityMetadata: EntityMetadataSchema,
  status: z.enum(['pending', 'approved', 'rejected', 'withdrawn']),
  requestor: RequestorSchema,
  stages: z.array(StageSchema),
  activeStageIndex: z.number(),
  ccEmails: z.array(z.email()),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export const ExecutionItemResponseSchema =
  createResponseSchema(ExecutionItemSchema);

export type ExecutionItem = z.infer<typeof ExecutionItemSchema>;

export const CheckFlowStatusQuerySchema = z.object({
  entityType: FlowTypesEnum,
  entityId: z.string(),
  entitySource: z.string(),
});

export const ProcessItemStatusesEnum = z.enum([
  'pending',
  'approved',
  'rejected',
  'withdrawn',
]);
export type ProcessItemStatuses = z.infer<typeof ProcessItemStatusesEnum>;

export const ProcessUserStatusesEnum = z.enum([
  'not_involved',
  'approver',
  'requestor',
]);
export type ProcessUserStatuses = z.infer<typeof ProcessUserStatusesEnum>;

export const CheckFlowStatusResponseSchema = createResponseSchema(
  z.object({
    userStatus: ProcessUserStatusesEnum,
    requestId: z.string().nullable(),
    requestStatus: ProcessItemStatusesEnum.nullable(),
    canCreateRequest: z.boolean().default(true), //TODO: remove the default value when the endpoint provides a real one
  }),
);
export type CheckFlowStatusResponse = z.infer<
  typeof CheckFlowStatusResponseSchema
>;

export const NotifyPartiesResponseSchema = createResponseSchema(
  z.object({
    status: z.string(),
  }),
);

export const NotifyPartiesUrlParamsSchema = z.object({
  requestId: z.string(),
});

export const SendApproverDecisionRequestSchema = z.object({
  comment: z.string(),
});
export type SendApproverDecisionRequest = z.infer<
  typeof SendApproverDecisionRequestSchema
>;

export const SendApproverDecisionUrlParamsSchema = z.object({
  requestId: z.string(),
  status: z.enum(['approve', 'reject']),
});
